import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, BedDouble, Bath, Maximize, Heart, Eye, Flame, Sparkles, Home, Building2, Warehouse, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useFavorites } from '@/contexts/FavoritesContext';
import { useComparison } from '@/contexts/ComparisonContext';

const PropertyCard = ({ property, onLocationClick }) => {
    const { isFavorite, toggleFavorite } = useFavorites();
    const { isInComparison, toggleComparison } = useComparison();
    const isFav = isFavorite(property.id);
    const isComparing = isInComparison(property.id);

    // Helper to get main image safely
    const mainImage = property.multimedia?.fotos?.[0]?.url || "https://via.placeholder.com/800x600?text=No+Image";

    // Icon based on type
    const TypeIcon = () => {
        switch (property.tipo) {
            case 'CASA': return <Home size={16} />;
            case 'APARTAMENTO': return <Building2 size={16} />;
            case 'TERRENO': return <Maximize size={16} />;
            case 'LOCAL': return <Store size={16} />;
            default: return <Home size={16} />;
        }
    };

    return (
        <div className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 flex flex-col h-full relative">
            {/* Image Container */}
            <div className="relative h-64 overflow-hidden">
                <img
                    src={mainImage}
                    alt={`Casa moderna en ${property.ubicacion.zona}, ${property.ubicacion.ciudad} - ${property.titulo}`}
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />

                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col gap-2 items-start">
                    <Badge className={`${property.operacion === 'VENTA' ? 'bg-blue-600' : 'bg-red-600'} text-white border-none px-3 py-1 shadow-md`}>
                        {property.operacion}
                    </Badge>
                    {property.esNueva && (
                        <Badge className="bg-green-500 text-white border-none px-3 py-1 shadow-md flex items-center gap-1">
                            <Sparkles size={12} /> Nuevo
                        </Badge>
                    )}
                    {(property.esTrending || (property.views > 100)) && (
                        <Badge variant="secondary" className="bg-orange-100 text-orange-800 hover:bg-orange-200">
                            <Flame size={12} /> Trending
                        </Badge>
                    )}
                    {/* HOA Removed */}
                    {property.estado !== 'DISPONIBLE' && (
                        <Badge className="bg-gray-800 text-white border-none px-3 py-1 shadow-md">
                            {property.estado}
                        </Badge>
                    )}
                </div>

                {/* Favorite Button */}
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        toggleFavorite(property);
                    }}
                    className="absolute top-4 right-4 p-2 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all duration-200 z-10"
                >
                    <Heart
                        className={`w-5 h-5 transition-colors ${isFav ? 'fill-red-500 text-red-500' : 'text-gray-600'}`}
                    />
                </button>

                {/* Overlay on Hover */}
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>

            {/* Content */}
            <div className="p-6 flex flex-col flex-grow">
                <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-1 text-sm font-semibold text-secondary uppercase tracking-wider">
                        <TypeIcon />
                        {property.tipo}
                    </div>
                    <div className="flex items-center text-gray-400 text-xs">
                        <Eye size={14} className="mr-1" />
                        {property.vistas} vistas
                    </div>
                </div>

                <h3
                    className="text-xl font-bold text-primary mb-2 line-clamp-2 min-h-[3.5rem] group-hover:text-secondary transition-colors"
                    title={property.titulo}
                >
                    {property.titulo}
                </h3>

                <div
                    className="flex items-center text-gray-500 mb-4 text-sm cursor-pointer hover:text-secondary transition-colors"
                    onClick={(e) => {
                        e.preventDefault();
                        if (onLocationClick) onLocationClick(property);
                    }}
                    title="Ver en mapa"
                >
                    <MapPin className="w-4 h-4 mr-1 text-secondary flex-shrink-0" />
                    <span className="truncate">{property.ubicacion.zona}, {property.ubicacion.ciudad}</span>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-3 gap-4 py-4 border-t border-b border-gray-100 mb-4">
                    <div className="flex flex-col items-center text-center">
                        <BedDouble className="w-5 h-5 text-gray-400 mb-1" />
                        <span className="text-sm font-bold text-gray-700">{property.dimensiones.habitaciones > 0 ? property.dimensiones.habitaciones : '--'}</span>
                        <span className="text-xs text-gray-500">Hab.</span>
                    </div>
                    <div className="flex flex-col items-center text-center border-l border-r border-gray-100">
                        <Bath className="w-5 h-5 text-gray-400 mb-1" />
                        <span className="text-sm font-bold text-gray-700">{property.dimensiones.banios > 0 ? property.dimensiones.banios : '--'}</span>
                        <span className="text-xs text-gray-500">Baños</span>
                    </div>
                    <div className="flex flex-col items-center text-center">
                        <Maximize className="w-5 h-5 text-gray-400 mb-1" />
                        <span className="text-sm font-bold text-gray-700">{property.dimensiones.metrosCuadrados || '--'}</span>
                        <span className="text-xs text-gray-500">m²</span>
                    </div>
                </div>

                {/* Price & Action */}
                <div className="mt-auto flex items-center justify-between">
                    <div>
                        <p className="text-xs text-gray-500 mb-0.5">Precio</p>
                        <p className="text-2xl font-bold text-primary">
                            ${property.precio.monto.toLocaleString()}
                        </p>
                    </div>
                    <Link to={`/propiedad/${property.id}`}>
                        <Button className="bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-all">
                            Ver Detalles
                        </Button>
                    </Link>
                </div>

                {/* Compare Checkbox */}
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center">
                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id={`compare-${property.id}`}
                            checked={isComparing}
                            onCheckedChange={() => toggleComparison(property)}
                        />
                        <label
                            htmlFor={`compare-${property.id}`}
                            className="text-xs text-gray-500 cursor-pointer select-none"
                        >
                            Comparar
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PropertyCard;
